import { redirect } from 'react-router';

import { apiIsomorphicFetchUserEnvironment } from 'api/userEnvironment/apiFetchUserEnvironment';
import { createI18nFn } from 'i18n/createI18nFn';
import { redirectRequireAuthentication } from 'modules/auth/routing/redirect-require-authentication';
import { orgDashboardManageListingsRoute } from 'modules/orgDashboard/routing/orgDashboard.routes';
import type { userDashboardSelectOrgRoute } from 'modules/userDashboard/routing/userDashboard.routes';
import type { HorizonAppRouteLoaderArgs } from 'routing/classes/types/AppRouteLoaderArgs';
import { createOrgRoute } from 'routing/routes';
import type { HorizonAppRouteComponentProps } from 'routing/types/HorizonAppRouteComponentProps';
import type { HorizonRouteMetaArgs } from 'routing/types/HorizonRouteMetaArgs';

import { UserDashboardSelectOrgPageLoadable } from './UserDashboardSelectOrgPageLoadable';

export async function loader({
  request,
  context,
}: HorizonAppRouteLoaderArgs<typeof userDashboardSelectOrgRoute>) {
  const [{ user }] = await Promise.all([
    apiIsomorphicFetchUserEnvironment({ request, context })(),
    UserDashboardSelectOrgPageLoadable.load(),
  ]);

  if (!user) return redirectRequireAuthentication({ request });

  if (user.orgs.length === 0) {
    return redirect(createOrgRoute.with({}));
  } else if (user.orgs.length === 1) {
    return redirect(
      orgDashboardManageListingsRoute.with({ orgId: user.orgs[0].id }),
    );
  }

  return { orgs: user.orgs, locale: context.locale };
}

export function meta({
  loaderData: { locale },
}: HorizonRouteMetaArgs<typeof loader>) {
  const i18n = createI18nFn(locale);
  return [{ title: i18n('Select Organization') }];
}

export default function UserDashboardSelectOrgHorizonRoute({
  loaderData: { orgs },
}: HorizonAppRouteComponentProps<typeof loader>) {
  return <UserDashboardSelectOrgPageLoadable orgs={orgs} />;
}
